import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { ApiService } from '@/services/api'
import type { OrdoreMiniLicense } from '@/services/api'
import { toast } from 'sonner'

interface RenewOrdoreMiniLicenseModalProps {
  license: OrdoreMiniLicense | null
  open: boolean
  onClose: () => void
  onSuccess: (license: OrdoreMiniLicense) => void
}

export default function RenewOrdoreMiniLicenseModal({ license, open, onClose, onSuccess }: RenewOrdoreMiniLicenseModalProps) {
  const [extendBy, setExtendBy] = useState('30')
  const [customDays, setCustomDays] = useState('')
  const [notes, setNotes] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setExtendBy('30')
      setCustomDays('')
      setNotes('')
      setError(null)
    }
  }, [open, license?._id])

  if (!license) return null

  const addDays = extendBy === 'custom' ? Number(customDays) : Number(extendBy)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!addDays || addDays <= 0) {
      setError('Please enter a valid number of days')
      return
    }

    setIsLoading(true)
    try {
      const response = await ApiService.renewOrdoreMiniLicense(license._id, {
        addDays,
        notes: notes || undefined
      })

      if (response.success) {
        toast.success(`License renewed — extended by ${addDays} days`)
        onSuccess(response.data)
        onClose()
      } else {
        setError(response.message || 'Failed to renew license')
        toast.error(response.message || 'Failed to renew license')
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to renew license'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Renew License
          </DialogTitle>
          <DialogDescription>
            {license.restaurantName} &middot; {license.licenseKey}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="extendBy">Extend by *</Label>
            <Select value={extendBy} onValueChange={setExtendBy}>
              <SelectTrigger id="extendBy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days</SelectItem>
                <SelectItem value="365">365 days</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {extendBy === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="customDays">Custom days</Label>
              <Input
                id="customDays"
                type="number"
                min={1}
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                placeholder="e.g., 45"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="renewNotes">Notes</Label>
            <Textarea
              id="renewNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Renew License
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
